import { useState, useCallback } from 'react';
import { Node, Beam } from '@/types/nnstructural2D';
import { CSVImportResult } from '@/utilities/CSVUtils';
import { 
  DuplicateGroupResult,
  findNodeDuplicateGroups,
  findBeamDuplicateGroups,
  resolveDuplicateGroups,
  getDuplicateGroupStats,
  getItemsForAppend
} from '@/utilities/duplicateGroupUtils';

export type ImportState = 
  | 'idle' 
  | 'file-selected' 
  | 'choice-dialog' 
  | 'processing' 
  | 'duplicate-review' 
  | 'completed' 
  | 'error';

interface ImportData<T> {
  importResult: CSVImportResult<T>;
  importChoice: 'replace' | 'append' | null;
  duplicateGroups: DuplicateGroupResult<T> | null;
  currentStep: ImportState;
}

export function useEnhancedCSVImport<T extends Node | Beam>(
  existingItems: T[],
  onImportComplete: (items: T[], mode: 'replace' | 'append') => void,
  itemType: 'nodes' | 'beams'
) {
  const [importData, setImportData] = useState<ImportData<T>>({
    importResult: { success: false, data: [], errors: [], warnings: [], preview: [], csv: '' },
    importChoice: null,
    duplicateGroups: null,
    currentStep: 'idle'
  });

  // Reset to initial state
  const resetImport = useCallback(() => {
    setImportData({
      importResult: { success: false, data: [], errors: [], warnings: [], preview: [], csv: '' },
      importChoice: null,
      duplicateGroups: null,
      currentStep: 'idle'
    });
  }, []);

  // Step 1: Handle successful CSV file parsing
  const handleFileImported = useCallback((result: CSVImportResult<T>) => {
    if (result.success && result.data.length > 0) {
      setImportData(prev => ({
        ...prev,
        importResult: result,
        currentStep: 'choice-dialog'
      }));
    } else {
      setImportData(prev => ({
        ...prev,
        importResult: result,
        currentStep: 'error'
      }));
    }
  }, []);

  // Step 2: Handle user choice (Replace or Append)
  const handleImportChoice = useCallback((choice: 'replace' | 'append') => {
    //console.log('🔧 handleImportChoice:', { choice, existingItems: existingItems.length, itemType });
    
    setImportData(prev => ({
      ...prev,
      importChoice: choice,
      currentStep: 'processing'
    }));

    // Process duplicates based on choice
    setTimeout(() => {
      try {
        const { importResult } = importData;
        //console.log('📊 Processing import:', { 
        //   importedItems: importResult.data.length, 
        //   choice, 
        //   existingItems: existingItems.length 
        // });
        
        let duplicateGroups: DuplicateGroupResult<T>;
        
        // For replace mode or empty canvas, don't check against existing items
        // For append mode, check against existing items to find duplicates
        const existingToCheck = (choice === 'replace' || existingItems.length === 0) ? [] : existingItems;
        //console.log('🔍 Checking duplicates against:', existingToCheck.length, 'existing items');
        //console.log('🔍 Existing items to check:', existingToCheck.map(item => `${item.id}: ${(item as any).x || 'N/A'},${(item as any).y || 'N/A'},${(item as any).z || 'N/A'}`));
        //console.log('🔍 Imported items to check:', importResult.data.map(item => `${item.id}: ${(item as any).x || 'N/A'},${(item as any).y || 'N/A'},${(item as any).z || 'N/A'}`));
        
        if (itemType === 'nodes') {
          duplicateGroups = findNodeDuplicateGroups(
            existingToCheck as Node[],
            importResult.data as Node[]
          ) as DuplicateGroupResult<T>;
        } else {
          duplicateGroups = findBeamDuplicateGroups(
            existingToCheck as Beam[],
            importResult.data as Beam[]
          ) as DuplicateGroupResult<T>;
        }

        //console.log('📋 Duplicate groups found:', duplicateGroups.groups.length);

        if (duplicateGroups.groups.length > 0) {
          // Found duplicates - show review dialog
          setImportData(prev => ({
            ...prev,
            duplicateGroups,
            currentStep: 'duplicate-review'
          }));
        } else {
          //console.log('✅ No duplicates - completing import directly');
          // No duplicates - complete import directly
          setImportData(prev => ({ ...prev, duplicateGroups, currentStep: 'completed' }));
          
          // Apply import directly
          let finalItems: T[];
          
          if (choice === 'replace' || existingItems.length === 0) {
            // For replace mode, use all imported data
            finalItems = importResult.data;
          } else {
            // For append mode, use filtered items that exclude ALL duplicates
            finalItems = getItemsForAppend(duplicateGroups);
            //console.log('🔗 Final items to import (append filtered):', finalItems.length);
            //console.log('🔗 Filtered items:', finalItems.map(item => `${item.id}: ${(item as any).x || 'N/A'},${(item as any).y || 'N/A'},${(item as any).z || 'N/A'}`));
          }
          
          if (choice === 'replace' || existingItems.length === 0) {
            onImportComplete(finalItems, 'replace');
          } else {
            // For append mode with filtered items, still need to check for ID collisions
            // (but not coordinate collisions - those are already filtered out)
            const existingIds = new Set(existingItems.map(item => item.id));
            const maxId = existingItems.length > 0 ? Math.max(...existingItems.map(item => parseInt(item.id) || 0)) : 0;
            
            const finalItemsWithUniqueIds = finalItems.map((item, index) => {
              if (existingIds.has(item.id)) {
                return { ...item, id: (maxId + index + 1).toString() } as T;
              }
              return item;
            });
            
            const combinedItems = [...existingItems, ...finalItemsWithUniqueIds];
            onImportComplete(combinedItems, 'append');
          }
          
          // Reset after a brief delay
          setTimeout(resetImport, 1000);
        }
      } catch (error) {
        console.error('❌ Error in handleImportChoice:', error);
        setImportData(prev => ({
          ...prev,
          currentStep: 'error'
        }));
      }
    }, 500);
  }, [importData, existingItems, itemType, onImportComplete, resetImport]);

  // Step 3: Handle duplicate resolution
  const handleDuplicateResolution = useCallback((
    resolutions: Record<number, Array<{ source: 'existing' | 'imported'; index: number }>>
  ) => {
    const { importChoice, duplicateGroups, importResult } = importData;
    
    if (!importChoice) return;

    let finalItems: T[] = [];

    if (!duplicateGroups || duplicateGroups.groups.length === 0) {
      // No duplicates - use all imported data
      finalItems = importResult.data;
    } else if (importChoice === 'append') {
      // APPEND MODE FIX:
      // We must NOT re-add any existing items selected in the resolution.
      // Only imported items (non-duplicates + user-kept imported duplicates) should be appended.
      // Existing items are already present in state.

      // Start with imported non-duplicate items
      finalItems = [...duplicateGroups.nonDuplicateItems];

      // For each duplicate group, add at most ONE imported item that the user chose to keep.
      duplicateGroups.groups.forEach(group => {
        const groupResolution = resolutions[group.id] || [];
        // Find imported selections in this group
        const importedChoices = groupResolution.filter(r => r.source === 'imported');
        if (importedChoices.length > 0) {
          // Take the first imported choice only to avoid creating new duplicates
          const first = importedChoices[0];
          const selectedImported = group.items.find(item => item.source === 'imported' && item.originalIndex === first.index);
          if (selectedImported) {
            finalItems.push(selectedImported.item as T);
          }
        }
      });
    } else {
      // REPLACE MODE (keep existing logic: selected items can include existing ones and they form the new list)
      const resolved = resolveDuplicateGroups(
        duplicateGroups.groups,
        duplicateGroups.nonDuplicateItems,
        resolutions
      );
      finalItems = resolved.finalItems as T[];
    }

    // Apply the import
    if (importChoice === 'replace' || existingItems.length === 0) {
      onImportComplete(finalItems, 'replace');
    } else {
      // Append mode - ensure unique IDs for imported items only
      const existingIds = new Set(existingItems.map(item => item.id));
      const maxId = existingItems.length > 0 ? Math.max(...existingItems.map(item => parseInt(item.id) || 0)) : 0;
      
      const finalItemsWithUniqueIds = finalItems.map((item, index) => {
        if (existingIds.has(item.id)) {
          return { ...item, id: (maxId + index + 1).toString() } as T;
        }
        return item;
      });
      
      const combinedItems = [...existingItems, ...finalItemsWithUniqueIds];
      onImportComplete(combinedItems, 'append');
    }

    setImportData(prev => ({
      ...prev,
      currentStep: 'completed'
    }));

    // Reset after a brief delay
    setTimeout(resetImport, 1000);
  }, [importData, existingItems, onImportComplete, resetImport]);

  // Cancel import at any stage
  const cancelImport = useCallback(() => {
    resetImport();
  }, [resetImport]);

  // Get statistics for current state
  const getImportStats = useCallback(() => {
    const { importResult, duplicateGroups } = importData;
    
    if (!importResult.success) return null;

    const stats = {
      totalImportItems: importResult.data.length,
      duplicateGroups: duplicateGroups ? getDuplicateGroupStats(duplicateGroups) : null
    };

    return stats;
  }, [importData]);

  return {
    // State
    currentStep: importData.currentStep,
    importResult: importData.importResult,
    importChoice: importData.importChoice,
    duplicateGroups: importData.duplicateGroups,
    
    // Actions
    handleFileImported,
    handleImportChoice,
    handleDuplicateResolution,
    cancelImport,
    resetImport,
    
    // Utils
    getImportStats,
    
    // Computed properties
    isIdle: importData.currentStep === 'idle',
    isProcessing: importData.currentStep === 'processing',
    showChoiceDialog: importData.currentStep === 'choice-dialog',
    showDuplicateReview: importData.currentStep === 'duplicate-review',
    isCompleted: importData.currentStep === 'completed',
    hasError: importData.currentStep === 'error',
    canProceed: importData.importResult.success && importData.importResult.data.length > 0
  };
}
